import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { SET_ADVERTISER, FETCH_REQUEST, CLEAR_CAMPAIGN_LIST } from '../../redux/advertiser/advertiserTypes';

import {
  fetchAdvertiserList,
  setSelectedAdvertiser,
  fetchCampaignsByAdvertiserGuid,
  fetchCampaignsForAdvertiser,
  fetchSkanCampaignsForAdvertiser,
  fetchJSTCampaignsForAdvertiser,
  fetchUserRoleByEmailId,
  fetchCTVCampaignsForAdvertiser,
  fetchGTVCampaignsForAdvertiser
} from '../../redux/advertiser/advertiserActions';
import { SET_ISSKAN } from '../../redux/campaign/campaignTypes';
import { resetReportData } from '../../redux/reporting/reportingActions';
import { resetCreativeData } from '../../redux/creative/creativeActions';
import { fetchPerformanceRequestError, resetScreenData } from '../../redux/campaign/campaignActions';
import { resetWowData } from '../../redux/wow/wowActions';
import { resetIAPData } from '../../redux/iap/iapActions';
import BASE_URL from '../../redux/config';

const AdvertisersFilter = ({
  fetchAdvertiserList,
  advertiserList,
  advertiser_guid,
  advertiser_name,
  campaign_list,
  fetchCampaignsForAdvertiser,
  loading,
  login,
  userEmail,
  isSkan,
  fetchSkanCampaignsForAdvertiser,
  advertiser_campaign_fetching,
  performanceLoading,
  widzardLoading,
  reportLoading,
  fetchPerformanceRequestError,
  resetScreenData,
  resetReportData,
  resetCreativeData,
  fetchJSTCampaignsForAdvertiser,
  fetchUserRoleByEmailId,
  resetWowData,
  resetIAPData,
  StartDate,
  EndDate,
  user_type
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedOption, setSelectedOption] = useState(null);
  const [disableAdvertiserDropDown, setDisableAdvertiserDropDown] = useState(false);
  const [previousPath, setPreviousPath] = useState(null);

  // Sanitize sfId
  const sanitizeSfId = (sfId) => {
    if (!sfId || sfId === 'undefined' || sfId === 'null' || sfId === null) {
      return '';
    }
    return sfId;
  };

  // Truncate text Function
  const truncateText = (text, maxLength = 40) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Check if user should see SFID
  const shouldShowSfid = () => {
    return user_type === 'admin' || user_type === 'internal';
  };

  // When leaving WoW, CTV, or GTV, refetch DSP campaigns (unless going to WoW, CTV, or GTV)
  useEffect(() => {
    const currentPath = location.pathname.split('/')[1];

    const leavingSpecialPath = previousPath === 'wow' || previousPath === 'ctv' || previousPath === 'gtv';
    const enteringSpecialPath = currentPath === 'wow' || currentPath === 'ctv' || currentPath === 'gtv';
    if (leavingSpecialPath && !enteringSpecialPath) {
      dispatch({ type: CLEAR_CAMPAIGN_LIST });

      const advertiser_guid = localStorage.getItem('advertiserId');
      const sfId = localStorage.getItem('sfId');

      if (advertiser_guid && StartDate && EndDate) {
        if (isSkan) {
          fetchSkanCampaignsForAdvertiser(advertiser_guid, sfId, StartDate, EndDate);
        } else {
          fetchCampaignsForAdvertiser(advertiser_guid, sfId, StartDate, EndDate);
        }
      }
    }

    setPreviousPath(currentPath);
  }, [location.pathname, StartDate, EndDate, isSkan]);

  useEffect(() => {
    const storedAdvertiserId = localStorage.getItem('advertiserId');
    const storedAdvertiserKey = localStorage.getItem('advertiserKey');
    const currentPath = window.location.pathname.split('/')[1];

    if (storedAdvertiserId && advertiserList.length > 0) {
      let selectedAdvertiserData;

      if (storedAdvertiserKey) {
        const [guid, sfIdFromKey] = storedAdvertiserKey.includes('_') ? storedAdvertiserKey.split('_') : [storedAdvertiserKey, ''];

        selectedAdvertiserData = advertiserList.find((option) => {
          const optionSfId = option.salesforce_account_id || '';
          return option.advertiser_guid === guid && (sfIdFromKey === '' || optionSfId === sfIdFromKey);
        });
      }

      if (!selectedAdvertiserData) {
        selectedAdvertiserData = advertiserList.find((option) => option.advertiser_guid === storedAdvertiserId);
      }

      if (selectedAdvertiserData) {
        const adv = selectedAdvertiserData;
        const rawSfId = adv.salesforce_account_id || adv.sfId || '';
        const cleanSfId = sanitizeSfId(rawSfId);

        setSelectedOption(adv);

        let data = {
          advertiser_guid: adv.advertiser_guid,
          advertiser_name: adv.advertiser_name,
          sfId: cleanSfId,
          timeZone: adv.timezone,
          startDate: adv.start_date,
          platform: adv.platform
        };

        dispatch({ type: SET_ADVERTISER, payload: data });

        const newAdvertiserKey = cleanSfId ? `${adv.advertiser_guid}_${cleanSfId}` : adv.advertiser_guid;
        localStorage.setItem('advertiserKey', newAdvertiserKey);
        localStorage.setItem('sfId', cleanSfId);

        // ONLY SKIP CAMPAIGN FETCH on WoW page
        if (currentPath === 'wow') {
          console.log('⏭️ On WoW page, skipping campaign fetch - WowQuarterDropDown handles it');
          return;
        }
        if (currentPath === 'ctv') {
          if (StartDate && EndDate) {
            console.log('🔍 AdvertiserFilter: Fetching CTV campaigns for date range', StartDate, EndDate);
            dispatch(fetchCTVCampaignsForAdvertiser(adv.advertiser_guid, cleanSfId, StartDate, EndDate));
          } else {
            console.warn('⚠️ CTV: date range not available for campaign list');
          }
          return;
        }
        if (currentPath === 'gtv') {
          if (StartDate && EndDate) {
            console.log('🔍 AdvertiserFilter: Fetching GTV campaigns for date range', StartDate, EndDate);
            dispatch(fetchGTVCampaignsForAdvertiser(adv.advertiser_guid, cleanSfId, StartDate, EndDate));
          } else {
            console.warn('⚠️ GTV: date range not available for campaign list');
          }
          return;
        }

        // Only fetch for non-WoW pages with T-2/T-1 dates
        if (StartDate && EndDate) {
          console.log('🔍 AdvertiserFilter: Fetching campaigns with T-2/T-1 dates:', {
            startDate: StartDate,
            endDate: EndDate,
            isSkan
          });

          if (isSkan) {
            fetchSkanCampaignsForAdvertiser(adv.advertiser_guid, cleanSfId, StartDate, EndDate);
          } else {
            fetchCampaignsForAdvertiser(adv.advertiser_guid, cleanSfId, StartDate, EndDate);
          }
        } else {
          console.warn('⚠️ AdvertiserFilter: T-2/T-1 dates not available');
        }
      }
    }
  }, [advertiserList, isSkan, StartDate, EndDate]);

  // SIMPLIFIED: Handle advertiser change from dropdown
  const handleInputChange = (event, value) => {
    if (value) {
      setSelectedOption(value);
      dispatch({ type: FETCH_REQUEST, payload: '' });
      fetchPerformanceRequestError('');

      const currentPath = window.location.pathname;
      const rawSfId = value.salesforce_account_id || value.sfId || '';
      const cleanSfId = sanitizeSfId(rawSfId);

      const advertiserKey = cleanSfId ? `${value.advertiser_guid}_${cleanSfId}` : value.advertiser_guid;

      const pathSegment = currentPath.split('/')[1];

      if (pathSegment === 'wow') {
        console.log('⏭️ On WoW page, skipping campaign fetch - WowQuarterDropDown will handle it');
      } else if (pathSegment === 'ctv') {
        console.log('🔍 AdvertiserFilter handleInputChange: Fetching CTV campaigns');
        dispatch(fetchCTVCampaignsForAdvertiser(value.advertiser_guid, cleanSfId, StartDate, EndDate));
      } else if (pathSegment === 'gtv') {
        console.log('🔍 AdvertiserFilter handleInputChange: Fetching GTV campaigns');
        dispatch(fetchGTVCampaignsForAdvertiser(value.advertiser_guid, cleanSfId, StartDate, EndDate));
      } else {
        console.log('🔍 AdvertiserFilter handleInputChange: Fetching campaigns with T-2/T-1');
        if (isSkan) {
          fetchSkanCampaignsForAdvertiser(value.advertiser_guid, cleanSfId, StartDate, EndDate);
        } else {
          fetchCampaignsForAdvertiser(value.advertiser_guid, cleanSfId, StartDate, EndDate);
        }
      }

      // Store all necessary values
      localStorage.setItem('advertiserKey', advertiserKey);
      localStorage.setItem('advertiserId', value.advertiser_guid);
      localStorage.setItem('sfId', cleanSfId);
      localStorage.setItem('timeZone', value.timezone);
      localStorage.setItem('startDate', value.start_date);
      localStorage.setItem('platform', JSON.stringify(value.platform));
      localStorage.setItem('selectedStartDate', StartDate);
      localStorage.setItem('selectedEndDate', EndDate);

      let data = {
        advertiser_guid: value.advertiser_guid,
        advertiser_name: value.advertiser_name,
        sfId: cleanSfId,
        timeZone: value.timezone,
        startDate: value.start_date,
        platform: value.platform,
        StartDate,
        EndDate
      };

      dispatch({ type: SET_ADVERTISER, payload: data });

      // Reset dependent state
      resetScreenData();
      resetReportData();
      resetCreativeData();
      resetWowData();
      resetIAPData();

      // Platform-based redirect logic
      const activePlatform = value.platform;
      if (activePlatform) {
        if (activePlatform.includes('IAP') && !activePlatform.includes('DSP')) {
          window.location.href = `${BASE_URL}/iap`;
        } else if (activePlatform.includes('DSP') && !activePlatform.includes('IAP')) {
          if (currentPath.includes('/iap')) {
            window.location.href = `${BASE_URL}/dashboard`;
          } else {
            window.location.href = `${BASE_URL}${currentPath}`;
          }
        } else if (activePlatform.includes('DSP') && activePlatform.includes('IAP')) {
          window.location.href = `${BASE_URL}${currentPath}`;
        }
      }
    }
  };

  // Fetch advertisers on login
  useEffect(() => {
    if (login === true && advertiserList.length === 0) {
      fetchAdvertiserList(userEmail);
      fetchUserRoleByEmailId(userEmail);
    }
  }, [login, advertiserList]);

  return (
    <>
      <Autocomplete
        value={selectedOption}
        disabled={disableAdvertiserDropDown}
        sx={{
          zIndex: '999999',
          minWidth: '500px',
          width: '100%',
          maxWidth: '700px'
        }}
        onChange={handleInputChange}
        options={advertiserList}
        renderOption={(props, option) => (
          <li {...props} style={{ padding: '10px 16px' }}>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: '500',
                  wordBreak: 'break-word',
                  lineHeight: '1.3'
                }}
              >
                {option.advertiser_name}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#666',
                  marginTop: '4px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                {shouldShowSfid() && <span style={{ color: '#0a50d2ff' }}>{option.salesforce_account_id}</span>}
                <span style={{ color: '#2e7d32' }}>{option.timezone}</span>
              </div>
            </div>
          </li>
        )}
        getOptionLabel={(option) => {
          if (!option) return 'Loading...';
          const displayName = truncateText(option.advertiser_name, 40);
          if (shouldShowSfid()) {
            return `${displayName} (${option.salesforce_account_id})`;
          } else {
            return displayName;
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Advertiser"
            variant="outlined"
            title={selectedOption ? selectedOption.advertiser_name : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                minWidth: '350px',
                maxWidth: '500px'
              },
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />
        )}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    loading: state.advertiserReducer.loading,
    advertiserList: state.advertiserReducer.advertiser_list,
    advertiser_guid: state.advertiserReducer.advertiser_guid,
    advertiser_name: state.advertiserReducer.advertiser_name,
    campaign_list: state.advertiserReducer.campaign_list,
    login: state.userReducer.login,
    userEmail: state.userReducer.userEmail,
    isSkan: state.campaignReducer.isSkan,
    advertiser_campaign_fetching: state.advertiserReducer.advertiser_campaign_fetching,
    performanceLoading: state.campaignReducer.performanceLoading,
    widzardLoading: state.campaignReducer.widzardLoading,
    reportLoading: state.campaignReducer.reportLoading,
    timeZone: state.advertiserReducer.timeZone,
    EndDate: state.campaignReducer.end_date,
    StartDate: state.campaignReducer.start_date,
    user_type: state.advertiserReducer.user_type
  };
};

const mapDispatchToProps = {
  fetchAdvertiserList,
  setSelectedAdvertiser,
  fetchCampaignsByAdvertiserGuid,
  fetchCampaignsForAdvertiser,
  fetchSkanCampaignsForAdvertiser,
  fetchPerformanceRequestError,
  resetScreenData,
  resetReportData,
  resetCreativeData,
  fetchJSTCampaignsForAdvertiser,
  fetchUserRoleByEmailId,
  resetWowData,
  resetIAPData
};

export default connect(mapStateToProps, mapDispatchToProps)(AdvertisersFilter);
